import { useDisclosure } from "@chakra-ui/hooks";
import AtriumApi from "api";
import DarkButton from "components/actions/DarkButton";
import GrayButton from "components/actions/GrayButton";
import TransparentButton from "components/actions/TransparentButton";
import Dialog from "components/dialog";
import BackIcon from "components/icons/BackIcon";
import StatusLabel from "components/label/StatusLabel";
import Loader from "components/loader/Loader";
import Switch from "components/switch";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const PlanningCenterLogo = () => {
  return (
    <svg
      className="inline-block"
      width="28"
      height="30"
      viewBox="0 0 28 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_690_12969)">
        <path
          d="M24.7852 3.27941C26.7556 3.79412 28 5.64706 28 7.60294V21.2941C28 23.25 26.6519 25 24.7852 25.6176L16.2815 28.1912C14.8296 28.6029 13.1704 28.6029 11.7185 28.1912L3.21481 25.7206C1.34815 25.1029 0 23.25 0 21.2941V7.60294C0 5.64706 1.34815 3.89706 3.21481 3.27941L11.7185 0.808824C13.1704 0.397059 14.8296 0.397059 16.2815 0.808824L24.7852 3.27941Z"
          fill="#2565F4"
        />
        <path
          d="M18.5 9.30005L13.7 10.7001C13.5 10.7001 13.4 10.8001 13.2 10.7001L8.4 9.30005C7.4 9.00005 6.5 9.70005 6.5 10.7001V21.5001C6.5 21.7001 6.6 21.9001 6.8 21.9001L8.6 22.4001C8.9 22.5001 9.1 22.3001 9.1 22.0001V19.5001C9.1 19.5001 9.1 19.4001 9.2 19.4001L12.8 20.5001C13.3 20.6001 13.8 20.6001 14.2 20.5001L19.6 18.8001C20.2 18.7001 20.5 18.3001 20.5 17.8001V10.8001C20.5 9.80005 19.5 9.00005 18.5 9.30005ZM17.9 16.5001C17.9 16.6001 17.8 16.7001 17.7 16.7001L13.8 17.9001C13.6 18.0001 13.4 18.0001 13.2 17.9001L9.4 16.8001C9.3 16.8001 9.2 16.7001 9.2 16.6001V12.6001C9.2 12.5001 9.3 12.4001 9.5 12.4001L12.6 13.3001C13.2 13.5001 13.9 13.5001 14.5 13.3001L17.6 12.4001C17.7 12.4001 17.9 12.5001 17.9 12.6001V16.5001Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_690_12969">
          <rect
            width="28"
            height="29"
            fill="white"
            transform="translate(0 0.5)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

const BreezeLogo = () => {
  return (
    <svg
      className="inline-block"
      width="20"
      height="28"
      viewBox="0 0 20 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.9109 26.4469V27.4832H0C0.0768758 26.709 0.107992 25.9279 0.239779 25.1606C0.517997 23.5415 1.13117 22.069 2.54056 20.9999C3.26722 20.4499 4.17875 20.0671 5.04269 19.9516C5.5735 21.5275 6.62596 22.6845 8.29344 23.2863C9.39349 23.6846 10.5155 23.7122 11.6375 23.3673C13.9401 22.6587 15.4667 20.5154 15.2434 18.3169C15.0127 16.0478 13.0725 14.2132 10.6583 13.9304C8.67599 13.6976 5.88283 14.7184 4.96581 17.8221C3.11164 17.4927 1.75716 16.5513 0.946305 14.9374C0.283708 13.6183 0.0640632 12.2027 0.0530809 10.7646C0.0256253 7.26953 0.043929 3.77616 0.0457594 0.281056C0.0457594 0.196567 0.0567417 0.112078 0.0658936 0H4.90175V11.0716C8.8334 8.17822 15.042 9.56627 17.9011 12.9579C20.6723 16.2461 20.7052 21.0172 17.9651 24.4278C15.4777 27.5246 9.38251 29.5627 4.9109 26.4469Z"
        fill="#007AFF"
      />
    </svg>
  );
};

const DisconnectDialog = ({ isOpen, onClose, onConfirm, integration }) => {
  return (
    <Dialog isOpen={isOpen} onClose={onClose} placement="top">
      <span className="flex items-center justify-center w-[56px] h-[56px] bg-gray-100 rounded-2xl">
        {integration === "Breeze" && <BreezeLogo />}
        {integration === "Planning Center" && <PlanningCenterLogo />}
      </span>

      <div className="text-lg font-bold dark:text-white mt-4">
        Disconnect {integration}
      </div>

      <p className="text-sm text-gray-700 mt-4">
        Are you sure you want to disconnect {integration} integration?
      </p>

      <div className="flex flex-row gap-4 mt-6">
        <DarkButton onClick={onConfirm}>Disconnect</DarkButton>
        <GrayButton onClick={onClose}>Cancel</GrayButton>
      </div>
    </Dialog>
  );
};

const PlanningCenterSettings = ({ back, handleDisconnect }) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [loading, setLoading] = useState(true);
  const [manualSyncLoading, setManualSyncLoading] = useState(false);
  const [isManualSync, isSetManualSync] = useState({});

  const [PCConnectionSettings, setPCConnectionSettings] = useState({
    campuses: false,
    people: false,
    households: false,
    volunteerRoles: false,
    checkins: false,
    attendanceRecords: false,
    serviceTemplate: false,
  });

  useEffect(() => {
    getPlanningCenterConnectionSettings();
  }, []);

  const getPlanningCenterConnectionSettings = async () => {
    try {
      setLoading(true);
      const response = await AtriumApi.get("/v1/sync");
      if (response) {
        setPCConnectionSettings({
          campuses: response?.data?.sync?.campuses || false,
          people: response?.data?.sync?.people || false,
          households: response?.data?.sync?.households || false,
          volunteerRoles: response?.data?.sync?.volunteerRoles || false,
          checkins: response?.data?.sync?.checkins || false,
          attendanceRecords: response?.data?.sync?.attendanceRecords || false,
          serviceTemplate: response?.data?.sync?.serviceTemplate || false,
        });
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleCheckboxChange = async (field) => {
    // Create new settings with toggled value
    const newSettings = {
      ...PCConnectionSettings,
      [field]: !PCConnectionSettings[field],
    };

    // Optimistically update UI
    setPCConnectionSettings(newSettings);

    try {
      setLoading(true);
      const response = await AtriumApi.post("/v1/sync", newSettings);

      if (response) {
        toast.success(`Status updated successfully.`);
      }
    } catch (error) {
      toast.error("Failed to update status.");
      // Revert on error
      setPCConnectionSettings((prev) => ({ ...prev }));
      toast.error(`Failed to update ${field} sync`);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSync = async () => {
    const payload = {
      source: "pc",
    };
    try {
      setManualSyncLoading(true);
      const response = await AtriumApi.post("/v1/manual-sync", payload, {});

      if (response) {
        toast.success(
          "Request sent successfully. Please note that syncing data may take some time and will be done in the background."
        );
      }
    } catch (error) {
      toast.error("Failed to sync data.");
    } finally {
      setManualSyncLoading(false);
    }
  };

  // mannual sync get api

  useEffect(() => {
    const fetchManualSync = async () => {
      try {
        const response = await AtriumApi.get("/v1/last-total-sync-data");
        if (response) {
          isSetManualSync(response?.data?.church);
        }
      } catch (error) {}
    };

    fetchManualSync();
  }, []);

  return (
    <div className="w-full">
      <div className="px-3 md:px-6 py-6">
        <GrayButton onClick={back}>
          <span className="inline-block align-middle mr-4">
            <BackIcon />
          </span>
          <span className="inline-block align-middle">Back</span>
        </GrayButton>

        <h2 className="font-bold text-xl mt-6">
          Planning Center <StatusLabel status="connected" />
        </h2>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      ) : (
        <>
          <div className="w-full max-w-[960px] block md:flex flex-row relative items-start rounded-[30px] p-3 md:p-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5">
            <div className="flex-1 mb-6">
              <h2 className="font-bold text-lg mb-2">Sync Configuration</h2>
              <p className="text-gray-700 text-sm">
                Choose which data needs to be synced with Atrium
              </p>
            </div>

            <div className="flex-2">
              <h2 className="font-bold text-base">User Data</h2>
              <div className="w-full flex flex-row font-medium text-sm border-b border-gray-100 dark:border-gray-700 py-4">
                <p className="w-24">
                  <Switch
                    checked={PCConnectionSettings.campuses}
                    onChange={() => handleCheckboxChange("campuses")}
                  />
                </p>
                <p className="flex-1 text-gray-700 text-sm">
                  {/* Sync member profiles (basic info, roles, status) */}
                  Sync campuses
                </p>
              </div>

              <div className="w-full flex flex-row font-medium text-sm border-b border-gray-100 dark:border-gray-700 py-4">
                <p className="w-24">
                  <Switch
                    checked={PCConnectionSettings.people}
                    onChange={() => handleCheckboxChange("people")}
                  />
                </p>
                <p className="flex-1 text-gray-700 text-sm">
                  {/* Sync family relationships */}
                  Sync people
                </p>
              </div>

              <div className="w-full flex flex-row font-medium text-sm border-b border-gray-100 dark:border-gray-700 py-4">
                <p className="w-24">
                  <Switch
                    checked={PCConnectionSettings.households}
                    onChange={() => handleCheckboxChange("households")}
                  />
                </p>
                <p className="flex-1 text-gray-700 text-sm">Sync households</p>
              </div>
              <div className="w-full flex flex-row font-medium text-sm border-b border-gray-100 dark:border-gray-700 py-4">
                <p className="w-24">
                  <Switch
                    disabled
                    // checked={PCConnectionSettings.households}
                    // onChange={() => handleCheckboxChange("households")}
                  />
                </p>
                <p className="flex-1 text-gray-700 text-sm">
                  Sync demographics and contact Information (Coming Soon)
                </p>
              </div>
              <div className="w-full flex flex-row font-medium text-sm py-4">
                <p className="w-24">
                  <Switch
                    disabled
                    // checked={PCConnectionSettings.people}
                    // onChange={() => handleCheckboxChange("people}")}
                  />
                </p>
                <p className="flex-1 text-gray-700 text-sm">
                  Sync family relationships (Coming Soon)
                </p>
              </div>

              <h2 className="font-bold text-base">Services and Events</h2>
              <div className="w-full flex flex-row font-medium text-sm border-b border-gray-100 dark:border-gray-700 py-4">
                <p className="w-24">
                  <Switch
                    checked={PCConnectionSettings.serviceTemplate}
                    onChange={() => handleCheckboxChange("serviceTemplate")}
                  />
                </p>
                <p className="flex-1 text-gray-700 text-sm">
                  Import service templates (sermon formats, event schedules)
                </p>
              </div>
              <div className="w-full flex flex-row font-medium text-sm py-4">
                <p className="w-24">
                  <Switch
                    checked={PCConnectionSettings?.volunteerRoles}
                    onChange={() => handleCheckboxChange("volunteerRoles")}
                  />
                </p>
                <p className="flex-1 text-gray-700 text-sm">
                  Sync volunteer role assignments within events
                </p>
              </div>

              <h2 className="font-bold text-base">Attendance Data</h2>
              <div className="w-full flex flex-row font-medium text-sm border-b border-gray-100 dark:border-gray-700 py-4">
                <p className="w-24">
                  <Switch
                    checked={PCConnectionSettings?.attendanceRecords}
                    onChange={() => handleCheckboxChange("attendanceRecords")}
                  />
                </p>
                <p className="flex-1 text-gray-700 text-sm">
                  Sync attendance records
                </p>
              </div>
              <div className="w-full flex flex-row font-medium text-sm py-4">
                <p className="w-24">
                  <Switch
                    checked={PCConnectionSettings?.checkins}
                    onChange={() => handleCheckboxChange("checkins")}
                  />
                </p>
                <p className="flex-1 text-gray-700 text-sm">
                  Import event check-ins for mapping attendance data
                </p>
              </div>
            </div>
          </div>

          <div className="w-full max-w-[960px] block md:flex flex-row relative items-start rounded-[30px] p-3 md:p-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5">
            <div className="flex-1 mb-6">
              <h2 className="font-bold text-lg mb-2">Manual Sync</h2>
            </div>

            <div className="flex-2 bg-gray-50 dark:bg-navy-900 py-5 px-3 md:px-8 rounded-2xl flex flex-row">
              <div className="flex-1">
                <p className="text-gray-700 text-sm">
                  {/* Last manual sync :{isManualSync?.updatedAt} */}
                  {isManualSync?.updatedAt &&
                    `Last manual sync: ${new Date(
                      isManualSync.updatedAt
                    ).toLocaleString(undefined, {
                      dateStyle: "long",
                      timeStyle: "short",
                    })}`}
                </p>
                <p className="text-gray-700 text-sm">
                  Current update count: {isManualSync?.totalSyncCount}
                </p>
                <p className="text-gray-700 text-sm">
                  Duplicate records found: {isManualSync?.duplicateCount}
                </p>
              </div>

              <DarkButton
                onClick={handleManualSync}
                disabled={manualSyncLoading}
              >
                {manualSyncLoading ? "Syncing..." : "Sync Now"}
              </DarkButton>
            </div>
          </div>

          <div className="w-full max-w-[960px] block md:flex flex-row relative items-start rounded-[30px] p-3 md:p-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5">
            <div className="flex-1 mb-6">
              <h2 className="font-bold text-lg mb-2">Disconnect</h2>
              <p className="text-gray-700 text-sm">
                You can disconnect integration and stop syncing. Existing data
                will be saved.
              </p>
            </div>

            <div className="flex-2 bg-gray-50 dark:bg-navy-900 py-5 px-3 md:px-8 rounded-2xl flex flex-row">
              <div className="flex-1">
                <h3 className="font-bold text-sm">Disconnect integration</h3>
                <p className="text-gray-700 text-sm">Syncing will be stopped</p>
              </div>

              <DarkButton onClick={onOpen}>Disconnect</DarkButton>
            </div>
          </div>
        </>
      )}

      <DisconnectDialog
        isOpen={isOpen}
        onClose={onClose}
        integration={"Planning Center"}
        onConfirm={handleDisconnect}
      />
    </div>
  );
};

const IntegrationSettings = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState("index");
  const [currentIntegration, setCurrentIntegration] = useState("");
  const connectClosure = useDisclosure({
    id: "connect",
    onClose: () => {
      // navigate("/admin/settings");
    },
  });

  const [planningCenterKeysDetails, setPlanningCenterKeysDetails] =
    useState(null);

  const [loading, setLoading] = useState(true);

  const [PCConnectionStatus, setPCConnectionStatus] = useState();

  const disconnectClosure = useDisclosure({
    id: "disconnect",
    onClose: () => {
      // navigate("/admin/settings");
    },
  });

  const connectBreeze = () => {
    setCurrentIntegration("Breeze");
    connectClosure.onOpen();
  };

  const disconnecttBreeze = () => {
    setCurrentIntegration("Breeze");
    disconnectClosure.onOpen();
  };

  const connectPlanningCenter = () => {
    setCurrentIntegration("Planning Center");
    connectClosure.onOpen();
  };

  const disconnectPlanningCenter = () => {
    setCurrentIntegration("Planning Center");
    disconnectClosure.onOpen();
  };

  useEffect(() => {
    getPlanningCenterConnectionStatus();
  }, []);

  const getPlanningCenterConnectionStatus = async () => {
    try {
      setLoading(true);
      const response = await AtriumApi.get("/v1/connect");
      if (response) {
        setPCConnectionStatus(response?.data);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };
  const handleDisconnect = async () => {
    try {
      const response = await AtriumApi.delete(`/v1/disconnect`);
      if (response) {
        toast.success("Successfully disconnected from Planning Center.");
        disconnectClosure.onClose();
        getPlanningCenterConnectionStatus();
        setPage("index");
      }
    } catch (error) {
      toast.error("Failed to disconnect from Planning Center.");
    }
  };

  useEffect(() => {
    const fetchPlanningCenterKeys = async () => {
      try {
        const response = await AtriumApi.get("/v1/planning-center-keys");
        if (response) {
          setPlanningCenterKeysDetails(response?.data?.planningCenterKeys);
        }
      } catch (error) {
        console.log("fetching all campuses error", error);
      }
    };
    fetchPlanningCenterKeys();
  }, []);

  const AUTH_URL =
    planningCenterKeysDetails &&
    `${process.env.REACT_APP_PC_BASE_URL}/oauth/authorize?client_id=${planningCenterKeysDetails?.clientId}&redirect_uri=${planningCenterKeysDetails?.redirectUri}&response_type=code&scope=people`;

  return (
    <>
      {page === "index" && (
        <div className="w-full flex flex-row gap-6 flex-wrap md:flex-nowrap">
          {loading ? (
            <div className="flex justify-center w-full py-10">
              <Loader />
            </div>
          ) : (
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-[40%] rounded-[30px] p-3 md:p-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5">
                <div className="w-full flex flex-row gap-3 items-start">
                  <span className="flex items-center justify-center w-[56px] h-[56px] bg-gray-100 rounded-2xl">
                    <PlanningCenterLogo />
                  </span>

                  <div className="flex-1">
                    <h2 className="font-bold text-xl mb-1">Planning Center</h2>
                    <p className="text-sm text-gray-700">API Integration</p>
                  </div>
                  {PCConnectionStatus?.isAccessTokenSet ? (
                    <StatusLabel status="connected" label="Connected" />
                  ) : (
                    <StatusLabel status="disabled" label="Disconnected" />
                  )}
                </div>

                <p className="my-6 text-gray-700 text-sm">
                  Organize information, coordinate events, communicate with your
                  team, and connect with your congregation.
                </p>

                {PCConnectionStatus?.isAccessTokenSet ? (
                  <TransparentButton onClick={() => setPage("planning-center")}>
                    Configure
                  </TransparentButton>
                ) : (
                  <DarkButton
                    onClick={() => {
                      if (planningCenterKeysDetails?.clientId) {
                        window.location.href = AUTH_URL;
                      } else {
                        toast.error(
                          "Please fill the details of Planning Center first."
                        );
                        navigate("/admin/settings?tab=integration_settings");
                      }
                    }}
                  >
                    Connect
                  </DarkButton>
                )}
              </div>
              {/* Right Box: Instructions */}
              <div className="w-full md:w-[60%] rounded-[30px] p-6 bg-white dark:bg-navy-800 shadow-sm border border-gray-200 dark:border-gray-700 h-fit">
                <h3 className="text-lg font-bold mb-3 text-center">
                  Instructions
                </h3>
                <h4 className="font-bold mt-">How to make connection</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Using redirect URI, create an app in the{" "}
                  <strong>Planning Center Developer Portal</strong> to get the{" "}
                  <strong>Client ID</strong> and <strong>Client Secret</strong>,
                  and attach these keys in integration settings.
                </p>

                <h4 className="font-bold mt-5">How to remove the app</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                  To remove the app, delete it from the Developer Portal. To
                  revoke access, go to your Planning Center profile under{" "}
                  <strong>"Authorized apps"</strong> and{" "}
                  <strong>"Applications"</strong>.
                </p>

                <h4 className="font-bold mt-5">Note:</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                  {" "}
                  Please ensure that you are logged into the correct{" "}
                  <strong>Planning Center Account </strong> in the same browser
                  from which you wish to retrieve your data. If you have
                  multiple <strong>Planning Center Accounts</strong> , make sure
                  you are logged into the specific account associated with the
                  data you intend to access.
                </p>
              </div>
            </div>
          )}

          {/* <div className="w-[470px] rounded-[30px] p-3 md:p-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5">
                    <div className="w-full flex flex-row gap-3 items-start flex-wrap">
                        <span className="flex items-center justify-center w-[56px] h-[56px] bg-gray-100 rounded-2xl">
                            <BreezeLogo/>
                        </span>

                        <div className="flex-1">
                            <h2 className="font-bold text-xl mb-1">Breeze</h2>
                            <p className="text-sm text-gray-700">API Integration</p>
                        </div>
                        <StatusLabel status="disabled" label="Disconnected" />
                    </div>

                    <p className="my-6 text-gray-700 text-sm">Manage member data, track donations, schedule events, and handle church communications efficiently.</p>

                    <DarkButton onClick={connectBreeze}>Connect</DarkButton>
                </div>             */}

          {/* connect form */}
          <Dialog
            isOpen={connectClosure.isOpen}
            onClose={connectClosure.onClose}
            placement="top"
          >
            <span className="flex items-center justify-center w-[56px] h-[56px] bg-gray-100 rounded-2xl">
              {currentIntegration === "Breeze" && <BreezeLogo />}
              {currentIntegration === "Planning Center" && (
                <PlanningCenterLogo />
              )}
            </span>

            <div className="text-lg font-bold dark:text-white mt-4">
              Connect {currentIntegration}
            </div>

            <p className="text-sm text-gray-700 mt-4">
              You’re about to be redirected to {currentIntegration} app. To
              complete the setup, please log in to your account and authorize
              access for secure data sharing.
            </p>

            <div className="flex flex-row gap-4 mt-6">
              <DarkButton>Continue</DarkButton>
              <GrayButton onClick={connectClosure.onClose}>Cancel</GrayButton>
            </div>
          </Dialog>
        </div>
      )}

      {page === "planning-center" && (
        <PlanningCenterSettings
          back={() => setPage("index")}
          handleDisconnect={handleDisconnect}
        />
      )}
    </>
  );
};

export default IntegrationSettings;
